import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { playEagleCry } from "@/lib/sound";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [state, setState] = useState({ loading: true, user: null, employee: null });
    // One cry per page load, at the moment a session first exists. Every way
    // into the app passes through here -- signing in, coming back from Google,
    // opening an already-signed-in app from the home screen -- so hooking it
    // once beats a call on each route that would drift apart and double up.
    const cried = useRef(false);

    // api is a stable module-level singleton — omitting from deps is intentional
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const refresh = useCallback(async () => {
        try {
            const r = await api.get("/auth/me");
            setState({ loading: false, user: r.data.user, employee: r.data.employee });
            if (r.data.employee && !cried.current) {
                cried.current = true;
                // Deliberately not awaited: whether a sound played has no
                // bearing on whether the app is usable, and a browser that
                // refuses it must not hold up the first screen.
                playEagleCry();
            }
        } catch (e) {
            setState({ loading: false, user: null, employee: null });
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch (e) {
            console.warn("Logout API error (continuing with local cleanup):", e?.message);
        }
        // Defensively clear the cookie client-side too, in case the server's
        // Set-Cookie didn't match attributes the browser used. Cookies with
        // SameSite=None; Secure can only be cleared by another SameSite=None; Secure cookie.
        try {
            document.cookie = "session_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=None; Secure";
            document.cookie = "session_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
        } catch (e) {
            console.warn("Cookie clear error:", e?.message);
        }
        setState({ loading: false, user: null, employee: null });
        window.location.href = "/login";
    };

    return (
        <AuthCtx.Provider value={{ ...state, refresh, logout }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
